import * as React from 'react';
import API_BASE_URL from './api';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import { getLangFromPath, buildPathWithLang } from './i18n/lang';
import { useLocation } from 'react-router-dom';

const TURNSTILE_SITE_KEY = '0x4AAAAAAEK4WB0WMr-ICm3t';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: 0,
  gap: 0,
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '420px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  borderRadius: 8,
  overflow: 'hidden'
}));

const RegisterContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  background: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
}));

export default function Register() {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [repeatPassword, setRepeatPassword] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState(false);
  const [repeatError, setRepeatError] = React.useState(false);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const turnstileContainerRef = React.useRef(null);
  const turnstileWidgetIdRef = React.useRef(null);

  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const loc = useLocation();
  const lang = getLangFromPath(loc.pathname);

  React.useEffect(() => {
    let cancelled = false;
    let script = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);

    const renderTurnstile = () => {
      if (
        cancelled ||
        !window.turnstile ||
        !turnstileContainerRef.current ||
        turnstileWidgetIdRef.current !== null
      ) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
        }
      );
    };

    if (window.turnstile) {
      renderTurnstile();
    } else if (script) {
      script.addEventListener('load', renderTurnstile, { once: true });
    } else {
      script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', renderTurnstile, { once: true });
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener('load', renderTurnstile);
      }
      if (turnstileWidgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (turnstileWidgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError(false);
    setPasswordError(false);
    setRepeatError(false);

    let valid = true;
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      valid = false;
    }
    if (!password || password.length < 6) {
      setPasswordError(true);
      setMessage(t('forms.errors.password_min', { min: 6 }));
      valid = false;
    } else if (password !== repeatPassword) {
      setRepeatError(true);
      setMessage(t('forms.errors.passwords_mismatch'));
      valid = false;
    }
    if (!valid) return;

    if (!turnstileToken) return;

    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const res = await fetch(`${API}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          password,
          locale: lang,
          turnstile_token: turnstileToken,
        }), // ← передаём текущую локаль без хардкода
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setMessage(t('auth.register_success'));
        setTimeout(() => navigate(buildPathWithLang(lang, '/login'), { replace: true }), 1200);
      } else {
        setMessage(result.message || result.detail || t('auth.register_error'));
      resetTurnstile();
      }
    } catch {
      setMessage(t('common.network_error'));
    resetTurnstile();
    }
  };

  return (
    <>
      {/*<CssBaseline enableColorScheme />*/}
      <RegisterContainer direction="column" alignItems="center">
        <Box mt={-1.5} width="100%">
          <Card variant="outlined">
            {/* Header */}
            <div
              style={{
                background: "rgb(0,110,69)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 32,
                padding: "22px 32px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{t('auth.register')}</span>
              <button
                type="button"
                onClick={() => {
                  const canGoBack =
                    typeof window !== 'undefined' && window.history && window.history.length > 1;
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    navigate(buildPathWithLang(lang, "/"));
                  }
                }}
                aria-label={t('common.close')}
                title={t('common.close')}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: 34,
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: 0,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
            {/* Форма */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: 'auto',
                gap: 2,
                padding: "32px 32px 12px 32px"
              }}
            >
              <TextField
                error={emailError}
                id="email"
                label={t('forms.labels.email')}
                type="email"
                value={email}
                placeholder={t('forms.labels.email')}
                autoComplete="email"
                onChange={e => setEmail(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{ mb: 2, borderRadius: 1, '& input': { height: 22, fontSize: 18 } }}
              />
              <TextField
                id="name"
                label={t('forms.labels.name')}
                type="text"
                value={name}
                placeholder={t('forms.labels.name')}
                onChange={e => setName(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{ mb: 2, borderRadius: 1, '& input': { height: 22, fontSize: 18 } }}
              />
              <TextField
                error={passwordError}
                id="password"
                label={t('forms.labels.password')}
                type="password"
                value={password}
                placeholder={t('forms.labels.password')}
                autoComplete="new-password"
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{ mb: 2, borderRadius: 1, '& input': { height: 22, fontSize: 18 } }}
              />
              <TextField
                error={repeatError}
                id="repeat-password"
                label={t('forms.labels.password_repeat')}
                type="password"
                value={repeatPassword}
                placeholder={t('forms.labels.password_repeat')}
                autoComplete="new-password"
                onChange={e => setRepeatPassword(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{ mb: 2, borderRadius: 1, '& input': { height: 22, fontSize: 18 } }}
              />
              <Box
                ref={turnstileContainerRef}
                sx={{ display: 'flex', justifyContent: 'center', minHeight: 65, mb: 1 }}
              />
              <Button
                type="submit"
                disabled={!turnstileToken}
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  background: "rgb(0,110,69)",
                  fontSize: 22,
                  height: 54,
                  fontWeight: 600,
                  borderRadius: 1,
                  letterSpacing: 1.2,
                  mb: 1,
                  '&:hover': { background: "#006b45" }
                }}
              >
                {t('auth.register').toUpperCase()}
              </Button>
            </Box>
            {message && (
              <Typography sx={{ color: '#e00', mt: 2, textAlign: 'center' }}>
                {message}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ textAlign: 'center', pb: 2, fontSize: 15 }}>
              {t('auth.have_account')}{' '}
              <Link
                component="button"
                variant="body2"
                sx={{ color: "rgb(0,110,69)", fontWeight: 500, fontSize: 15, textDecoration: "none" }}
                onClick={() => navigate(buildPathWithLang(lang, "/login"))}
              >
                {t('auth.login')}
              </Link>
            </Typography>
          </Card>
        </Box>
      </RegisterContainer>
    </>
  );
}
